<template>
  <div class="fts-connections">
    <div class="connection-list-header" :class="{ 'connection-list-header--embedded': embedded }">
      <!-- Hidden when embedded: the settings page that hosts this already titles it. -->
      <div class="connection-list-header__l" v-if="!embedded">
        <div class="connection-list-title">Connections</div>
        <div class="connection-list-subtitle">
          Personal access tokens for the Inistate API and MCP. A connection acts as you, limited
          to the scopes and workspaces you grant it.
        </div>
      </div>

      <div class="connection-list-header__r">
        <fts-tippy content="Refresh">
          <fts-btn
            slot="trigger"
            icon
            text
            :disabled="loading"
            @click="load"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </fts-btn>
        </fts-tippy>

        <fts-btn
          color="primary"
          text-transform-init
          :disabled="notEnabled || notPermitted"
          @click="openCreate"
        >
          <font-awesome-icon :icon="['fas', 'plus']" class="connection-btn-icon" />
          New connection
        </fts-btn>
      </div>
    </div>

    <div class="connection-list-body">
      <!-- pricing gate: seat tier does not include the feature; existing rows
           stay visible so a downgraded user can still revoke them -->
      <div class="connection-plan-banner" v-if="notPermitted && !loading">
        <font-awesome-icon :icon="['fas', 'lock']" class="connection-plan-banner-icon" />
        <div class="connection-plan-banner-text">
          API connections are not included in your current membership plan.
          New connections and rotation are disabled, and existing tokens stop
          authenticating — you can still revoke them. Ask your workspace
          contact about upgrading your seat.
        </div>
      </div>

      <div class="connection-list-loading" v-if="loading">
        <fts-spinner />
      </div>

      <div class="connection-list-empty" v-else-if="notEnabled">
        <font-awesome-icon :icon="['fas', 'plug']" size="2x" class="connection-empty-icon" />
        <div class="connection-empty-title">API connections are not enabled</div>
        <div class="connection-empty-desc">
          This environment does not have API connections switched on. Contact your administrator.
        </div>
      </div>

      <div class="connection-list-empty" v-else-if="!connections.length && !notPermitted">
        <font-awesome-icon :icon="['fas', 'plug']" size="2x" class="connection-empty-icon" />
        <div class="connection-empty-title">No connections yet</div>
        <div class="connection-empty-desc">
          Create a connection to get a scoped access token for integrations, scripts, and MCP.
        </div>
      </div>

      <div class="connection-list-table" v-else-if="connections.length">
        <div class="connection-row connection-row--head">
          <div class="connection-cell connection-cell--name">Connection</div>
          <div class="connection-cell connection-cell--token">Token</div>
          <div class="connection-cell connection-cell--scopes">Scopes</div>
          <div class="connection-cell connection-cell--access">Access</div>
          <div class="connection-cell connection-cell--used">Last used</div>
          <div class="connection-cell connection-cell--status">Status</div>
          <div class="connection-cell connection-cell--actions"></div>
        </div>

        <div
          class="connection-row"
          :class="{ 'connection-row--revoked': x.status !== 'active' }"
          v-for="x in connections"
          :key="x.id"
        >
          <div class="connection-cell connection-cell--name">
            <span class="connection-color" :style="{ background: x.color || '#7c5cd1' }"></span>
            <div class="connection-name-group">
              <div class="connection-name">{{ x.name }}</div>
              <div class="connection-desc" v-if="x.description">{{ x.description }}</div>
            </div>
          </div>

          <div class="connection-cell connection-cell--token">
            <code class="connection-token-fragment">{{ x.tokenPrefix }}&hellip;{{ x.tokenLast4 }}</code>
          </div>

          <div class="connection-cell connection-cell--scopes">
            <fts-tippy :content="x.scopes.join(', ')">
              <fts-badge slot="trigger" color="text" variant="pill">
                {{ x.scopes.length }} scope{{ x.scopes.length === 1 ? '' : 's' }}
              </fts-badge>
            </fts-tippy>
          </div>

          <div class="connection-cell connection-cell--access">
            {{ accessSummary(x) }}
          </div>

          <div class="connection-cell connection-cell--used">
            {{ x.lastUsedAt ? fromNow(x.lastUsedAt) : 'Never' }}
          </div>

          <div class="connection-cell connection-cell--status">
            <fts-badge :color="x.status === 'active' ? 'success' : 'danger'" variant="pill">
              {{ x.status }}
            </fts-badge>
          </div>

          <div class="connection-cell connection-cell--actions">
            <template v-if="x.status === 'active'">
              <fts-tippy content="Edit scopes &amp; access">
                <fts-btn slot="trigger" icon text @click="openEdit(x)">
                  <font-awesome-icon :icon="['fas', 'pen']" />
                </fts-btn>
              </fts-tippy>
              <fts-tippy content="Rotate token">
                <fts-btn slot="trigger" icon text @click="askRotate(x)">
                  <font-awesome-icon :icon="['fas', 'redo-alt']" />
                </fts-btn>
              </fts-tippy>
              <fts-tippy content="Revoke">
                <fts-btn slot="trigger" icon text @click="askRevoke(x)">
                  <font-awesome-icon :icon="['fas', 'ban']" />
                </fts-btn>
              </fts-tippy>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- create / edit -->
    <fts-client-modal
      :title="editing ? 'Edit connection' : 'New connection'"
      @closed="resetForm"
      ref="editModal"
    >
      <font-awesome-icon slot="icon" :icon="['fas', 'plug']" fixed-width />
      <template #body>
        <div class="connection-form">
          <div class="connection-form-label">Name</div>
          <input
            type="text"
            class="fts-form-control"
            placeholder="e.g. reporting-sync"
            maxlength="60"
            v-model.trim="form.name"
          />

          <div class="connection-form-label">Description <span class="connection-form-optional">(optional)</span></div>
          <input
            type="text"
            class="fts-form-control"
            placeholder="What is this connection for?"
            maxlength="200"
            v-model.trim="form.description"
          />

          <div class="connection-form-label">Scopes</div>
          <div class="connection-form-helper">What this connection is allowed to do.</div>
          <div class="connection-scope-list">
            <label class="connection-scope-item" v-for="s in scopeMeta" :key="s.value">
              <input type="checkbox" :value="s.value" v-model="form.scopes" />
              <div class="connection-scope-text">
                <code class="connection-scope-code">{{ s.value }}</code>
                <div class="connection-scope-desc">{{ s.description }}</div>
              </div>
            </label>
          </div>

          <div class="connection-form-label">Access</div>
          <div class="connection-form-helper">Which workspaces this connection can reach.</div>
          <label class="connection-access-option">
            <input type="radio" value="all" v-model="form.access" />
            <span>All current and future workspaces</span>
          </label>
          <label class="connection-access-option">
            <input type="radio" value="specific" v-model="form.access" />
            <span>Specific workspaces</span>
          </label>

          <template v-if="form.access === 'specific'">
            <input
              type="search"
              class="fts-form-control connection-workspace-search"
              placeholder="Search workspaces"
              v-model="workspaceSearch"
              @input="searchWorkspaces"
            />
            <div class="connection-workspace-list">
              <label class="connection-workspace-item" v-for="w in workspaces" :key="w.id">
                <input type="checkbox" :value="w.id" v-model="form.workspaceIds" />
                <span>{{ w.name }}</span>
              </label>
              <div class="connection-form-helper" v-if="!workspaces.length && !workspacesLoading">
                {{ workspaceSearch ? 'No workspace matches your search.' : 'No workspaces found for your account.' }}
              </div>
              <fts-btn
                text
                sm
                text-transform-init
                class="connection-workspace-more"
                :loading="workspacesLoading"
                @click="loadWorkspaces({ more: true })"
                v-if="workspacesHasMore"
              >
                Load more
              </fts-btn>
            </div>
            <div class="connection-form-helper" v-if="form.workspaceIds.length">
              {{ form.workspaceIds.length }} selected
            </div>
          </template>

          <div class="connection-form-error" v-if="formError">{{ formError }}</div>
        </div>
      </template>
      <template #footerRight>
        <fts-btn text text-transform-init @click="$refs.editModal.close()">Cancel</fts-btn>
        <fts-btn
          color="primary"
          text-transform-init
          :loading="submitting"
          @click="save"
        >
          {{ editing ? 'Save changes' : 'Create connection' }}
        </fts-btn>
      </template>
    </fts-client-modal>

    <!-- token reveal (create / rotate result) -->
    <fts-client-modal
      title="Copy your access token"
      @closed="tokenReveal = null"
      ref="tokenModal"
    >
      <font-awesome-icon slot="icon" :icon="['fas', 'key']" fixed-width />
      <template #body>
        <template v-if="tokenReveal">
          <div class="connection-token-warning">
            This token is shown <strong>only once</strong>. Store it somewhere safe — you can
            rotate the connection later, but this value cannot be displayed again.
          </div>
          <fts-clipboard :copy="tokenReveal.token" class="connection-token-clipboard">
            <code class="connection-token-value">{{ tokenReveal.token }}</code>
          </fts-clipboard>
          <div class="connection-token-usage">
            Use it as <code>Authorization: Bearer {{ tokenReveal.token.slice(0, 12) }}&hellip;</code>
            against <code>/v1</code> endpoints.
          </div>
        </template>
      </template>
      <template #footerRight>
        <fts-btn color="primary" text-transform-init @click="$refs.tokenModal.close()">
          I've copied it
        </fts-btn>
      </template>
    </fts-client-modal>

    <!-- rotate confirm -->
    <fts-client-modal
      title="Rotate token?"
      @closed="target = null"
      ref="rotateModal"
    >
      <font-awesome-icon slot="icon" :icon="['fas', 'redo-alt']" fixed-width />
      <template #body>
        <div class="confirmation-desc" v-if="target">
          A new token will be issued for <strong>{{ target.name }}</strong>. The current token
          keeps working for 24 hours so you can switch integrations without downtime.
        </div>
      </template>
      <template #footerRight>
        <fts-btn text text-transform-init @click="$refs.rotateModal.close()">Cancel</fts-btn>
        <fts-btn color="primary" text-transform-init :loading="submitting" @click="rotate">
          Rotate
        </fts-btn>
      </template>
    </fts-client-modal>

    <!-- revoke confirm -->
    <fts-client-modal
      title="Revoke connection?"
      @closed="target = null"
      ref="revokeModal"
    >
      <font-awesome-icon slot="icon" :icon="['fas', 'ban']" fixed-width />
      <template #body>
        <div class="confirmation-desc" v-if="target">
          <strong>{{ target.name }}</strong> will stop working immediately, including any rotation
          overlap. This cannot be undone.
        </div>
      </template>
      <template #footerRight>
        <fts-btn text text-transform-init @click="$refs.revokeModal.close()">Cancel</fts-btn>
        <fts-btn color="danger" text-transform-init :loading="submitting" @click="revoke">
          Revoke
        </fts-btn>
      </template>
    </fts-client-modal>
  </div>
</template>

<script>
import moment from 'moment';

import { clientStore } from '../store/clientStore';

import FTSBadge from '../elements/FTSBadge';
import FTSBtn from '../elements/FTSBtn';
import FTSClientModal from '../elements/FTSClientModal';
import FTSClipboard from '../elements/FTSClipboard';
import FTSSpinner from '../elements/FTSSpinner';
import FTSTippy from '../elements/FTSTippy';

/* SS06304: GET /api/workspace answers 10 workspaces a page. */
const WORKSPACE_PAGE_SIZE = 10;
const WORKSPACE_SEARCH_DELAY_MS = 300;

const SCOPE_META = [
  { value: 'data.entries:read', description: 'See entries, history, and forms' },
  { value: 'data.entries:write', description: 'Perform activities to create and update entries' },
  { value: 'data.files:read', description: 'Download files attached to entries' },
  { value: 'data.files:write', description: 'Upload files' },
  { value: 'schema.modules:read', description: 'See workspaces, modules, and their structure' },
  { value: 'schema.modules:write', description: 'Create and modify module designs' },
  { value: 'webhooks:manage', description: 'Register and remove automation webhooks' },
  { value: 'user:read', description: 'See workspace members, for assigning entries' },
];

const emptyForm = () => ({
  name: '',
  description: '',
  scopes: ['data.entries:read'],
  access: 'all',
  workspaceIds: [],
});

export default {
  name: 'FTSConnections',
  props: {
    // Default-off. True when mounted inside Settings > Your account > Connections, which
    // supplies its own heading; the /connections route renders exactly as before.
    embedded: { type: Boolean, default: false },
  },
  components: {
    'fts-badge': FTSBadge,
    'fts-btn': FTSBtn,
    'fts-client-modal': FTSClientModal,
    'fts-clipboard': FTSClipboard,
    'fts-spinner': FTSSpinner,
    'fts-tippy': FTSTippy,
  },
  data() {
    return {
      loading: false,
      notEnabled: false,
      // Pricing gate: feature is on, but this user's seat tier doesn't include it.
      notPermitted: false,
      connections: [],
      workspaces: [],
      workspaceSearch: '',
      workspacePage: 0,
      workspacesHasMore: false,
      workspacesLoading: false,
      workspaceRequest: 0,
      workspaceSearchTimer: null,
      scopeMeta: SCOPE_META,

      editing: null,
      form: emptyForm(),
      formError: '',
      submitting: false,

      tokenReveal: null,
      target: null,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.notEnabled = false;
      this.notPermitted = false;
      try {
        // Seat-tier availability first: the list itself still loads for a
        // non-qualifying user (rows stay revocable), but create/rotate are
        // disabled and the plan banner explains why.
        try {
          const availability = await clientStore.dispatch('getConnectionsAvailability');
          const data = availability && availability.data;
          if (data && data.enabled === false) {
            this.notEnabled = true;
            this.connections = [];
            return;
          }
          this.notPermitted = !!(data && data.enabled && !data.permitted);
        } catch (availabilityErr) {
          // Older backend without the probe: fall through to the list call.
        }

        const res = await clientStore.dispatch('getApiConnections');
        this.connections = res.data || [];
      } catch (err) {
        if (err && err.response && err.response.status === 404) {
          this.notEnabled = true;
        }
        this.connections = [];
      } finally {
        this.loading = false;
      }
    },
    /* SS06304: the picker showed only the first page of 10 and had no search, so a workspace
       beyond it could not be granted. It now searches by name and loads further pages. The
       ticks live in form.workspaceIds, not in the list, so they stay while the search changes.
       Only the newest request may fill the list: a slow answer to an earlier search or page
       must not overwrite it (the workspace-switcher race of 2026-10-01). */
    async loadWorkspaces({ more = false } = {}) {
      const page = more ? this.workspacePage + 1 : 0;
      const request = ++this.workspaceRequest;
      this.workspacesLoading = true;
      try {
        const res = await clientStore.dispatch('getApiConnectionWorkspaces', {
          search: this.workspaceSearch || '',
          page,
        });
        if (request !== this.workspaceRequest) return;
        const rows = (res.data || []).map((w) => ({ id: w.id, name: w.name }));
        const seen = new Set(more ? this.workspaces.map((w) => w.id) : []);
        this.workspaces = (more ? this.workspaces : []).concat(rows.filter((w) => !seen.has(w.id)));
        this.workspacePage = page;
        this.workspacesHasMore = rows.length >= WORKSPACE_PAGE_SIZE;
      } catch (err) {
        if (request !== this.workspaceRequest) return;
        if (!more) this.workspaces = [];
        this.workspacesHasMore = false;
      } finally {
        if (request === this.workspaceRequest) this.workspacesLoading = false;
      }
    },
    searchWorkspaces() {
      clearTimeout(this.workspaceSearchTimer);
      this.workspaceSearchTimer = setTimeout(() => this.loadWorkspaces(), WORKSPACE_SEARCH_DELAY_MS);
    },
    resetWorkspaceSearch() {
      clearTimeout(this.workspaceSearchTimer);
      this.workspaceSearch = '';
      this.loadWorkspaces();
    },
    openCreate() {
      this.editing = null;
      this.form = emptyForm();
      this.formError = '';
      this.resetWorkspaceSearch();
      this.$refs.editModal.show();
    },
    openEdit(connection) {
      this.editing = connection;
      this.form = {
        name: connection.name,
        description: connection.description || '',
        scopes: [...connection.scopes],
        access: connection.allWorkspaces ? 'all' : 'specific',
        workspaceIds: [...(connection.workspaceIds || [])],
      };
      this.formError = '';
      this.resetWorkspaceSearch();
      this.$refs.editModal.show();
    },
    validate() {
      if (!this.form.name) return 'A connection name is required.';
      if (!this.form.scopes.length) return 'Select at least one scope.';
      if (this.form.access === 'specific' && !this.form.workspaceIds.length)
        return 'Select at least one workspace, or grant all workspaces.';
      return '';
    },
    async save() {
      this.formError = this.validate();
      if (this.formError) return;

      const payload = {
        name: this.form.name,
        description: this.form.description || null,
        scopes: this.form.scopes,
        allWorkspaces: this.form.access === 'all',
        workspaceIds: this.form.access === 'all' ? [] : this.form.workspaceIds,
      };

      this.submitting = true;
      try {
        if (this.editing) {
          await clientStore.dispatch('updateApiConnection', { id: this.editing.id, payload });
          this.$refs.editModal.close();
        } else {
          const res = await clientStore.dispatch('createApiConnection', payload);
          this.$refs.editModal.close();
          this.tokenReveal = { token: res.data.token, name: res.data.connection.name };
          this.$refs.tokenModal.show();
        }
        await this.load();
      } catch (err) {
        this.formError = this.apiError(err);
      } finally {
        this.submitting = false;
      }
    },
    askRotate(connection) {
      this.target = connection;
      this.$refs.rotateModal.show();
    },
    async rotate() {
      if (!this.target) return;
      this.submitting = true;
      try {
        const res = await clientStore.dispatch('rotateApiConnection', this.target.id);
        this.$refs.rotateModal.close();
        this.tokenReveal = { token: res.data.token, name: res.data.connection.name };
        this.$refs.tokenModal.show();
        await this.load();
      } catch (err) {
        // e.g. pricing gate (403 connections_not_permitted) — surface the
        // backend's message; keep the modal open.
        this.$toasted.global.error({
          response: err.response,
          message: this.apiError(err),
        });
      } finally {
        this.submitting = false;
      }
    },
    askRevoke(connection) {
      this.target = connection;
      this.$refs.revokeModal.show();
    },
    async revoke() {
      if (!this.target) return;
      this.submitting = true;
      try {
        await clientStore.dispatch('revokeApiConnection', this.target.id);
        this.$refs.revokeModal.close();
        await this.load();
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.editing = null;
      this.form = emptyForm();
      this.formError = '';
    },
    accessSummary(connection) {
      if (connection.allWorkspaces) return 'All workspaces';
      const count = (connection.workspaceIds || []).length;
      return `${count} workspace${count === 1 ? '' : 's'}`;
    },
    fromNow(value) {
      return moment.utc(value).local().fromNow();
    },
    apiError(err) {
      if (err && err.response && err.response.data && err.response.data.message)
        return err.response.data.message;
      return 'Something went wrong. Please try again.';
    },
  },
};
</script>

<style lang="scss">
.fts-connections {
  height: 100%;
  overflow: auto;
  padding: 24px 32px;

  .connection-list-header {
    // Embedded in Settings: the host page owns the title, so only the actions remain and
    // they keep their place on the right. Nested, so it outranks the space-between below
    // rather than losing to it on source order.
    &.connection-list-header--embedded {
      justify-content: flex-end;
    }

    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 20px;

    .connection-list-title {
      font-size: 20px;
      font-weight: 600;
    }

    .connection-list-subtitle {
      margin-top: 4px;
      font-size: 13px;
      color: rgba(0, 0, 0, 0.55);
      max-width: 560px;
    }

    .connection-list-header__r {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }

    .connection-btn-icon {
      margin-right: 6px;
    }
  }

  .connection-list-loading,
  .connection-plan-banner {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    margin-bottom: 16px;
    border: 1px solid rgba(224, 168, 0, 0.35);
    border-radius: 6px;
    background: rgba(255, 200, 50, 0.12);
    color: rgba(0, 0, 0, 0.7);

    .connection-plan-banner-icon {
      margin-top: 2px;
      color: #b8860b;
    }

    .connection-plan-banner-text {
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .connection-list-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 64px 0;
    color: rgba(0, 0, 0, 0.55);

    .connection-empty-icon {
      opacity: 0.35;
      margin-bottom: 12px;
    }

    .connection-empty-title {
      font-weight: 600;
      color: rgba(0, 0, 0, 0.75);
      margin-bottom: 4px;
    }

    .connection-empty-desc {
      font-size: 13px;
      max-width: 420px;
      text-align: center;
    }
  }

  .connection-list-table {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    overflow: hidden;
  }

  .connection-row {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    font-size: 13px;

    &.connection-row--head {
      border-top: none;
      background: rgba(0, 0, 0, 0.02);
      font-weight: 600;
      color: rgba(0, 0, 0, 0.6);
    }

    &.connection-row--revoked {
      opacity: 0.55;
    }
  }

  .connection-cell {
    &.connection-cell--name { flex: 2 1 0; display: flex; align-items: center; min-width: 0; }
    &.connection-cell--token { flex: 1.2 1 0; }
    &.connection-cell--scopes { flex: 0.9 1 0; }
    &.connection-cell--access { flex: 1 1 0; }
    &.connection-cell--used { flex: 1 1 0; }
    &.connection-cell--status { flex: 0.7 1 0; }
    &.connection-cell--actions { flex: 0 0 110px; text-align: right; white-space: nowrap; }
  }

  .connection-color {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  .connection-name-group {
    min-width: 0;

    .connection-name {
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .connection-desc {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.5);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .connection-token-fragment {
    font-size: 12px;
    background: rgba(0, 0, 0, 0.04);
    padding: 2px 6px;
    border-radius: 4px;
  }
}

.connection-form {
  .connection-form-label {
    font-weight: 600;
    font-size: 13px;
    margin: 14px 0 4px;

    &:first-child { margin-top: 0; }

    .connection-form-optional {
      font-weight: 400;
      color: rgba(0, 0, 0, 0.45);
    }
  }

  .connection-form-helper {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 6px;
  }

  .connection-form-error {
    margin-top: 12px;
    font-size: 13px;
    color: #d64545;
  }

  .connection-scope-list {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    max-height: 220px;
    overflow: auto;
  }

  .connection-scope-item {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 8px 12px;
    margin: 0;
    cursor: pointer;
    border-top: 1px solid rgba(0, 0, 0, 0.05);

    &:first-child { border-top: none; }
    &:hover { background: rgba(0, 0, 0, 0.02); }

    input { margin-top: 3px; }

    .connection-scope-code {
      font-size: 12px;
    }

    .connection-scope-desc {
      font-size: 12px;
      color: rgba(0, 0, 0, 0.55);
    }
  }

  .connection-access-option {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 0;
    font-size: 13px;
    cursor: pointer;
  }

  .connection-workspace-search {
    margin-top: 6px;
  }

  .connection-workspace-list {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    max-height: 200px;
    overflow: auto;
    margin-top: 6px;

    .connection-workspace-more {
      margin: 4px 8px;
    }

    .connection-workspace-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      margin: 0;
      font-size: 13px;
      cursor: pointer;
      border-top: 1px solid rgba(0, 0, 0, 0.05);

      &:first-child { border-top: none; }
      &:hover { background: rgba(0, 0, 0, 0.02); }
    }
  }
}

.connection-token-warning {
  font-size: 13px;
  background: #fff7e6;
  border: 1px solid #ffe1a8;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.connection-token-clipboard {
  display: block;

  .connection-token-value {
    display: block;
    word-break: break-all;
    font-size: 13px;
    background: rgba(0, 0, 0, 0.04);
    padding: 8px 10px;
    border-radius: 6px;
  }
}

.connection-token-usage {
  margin-top: 10px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
}
</style>
